import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Box, Typography } from '@mui/material';
import api from '../services/api';
import MovieCard from './MovieCard';

const PAGE_SIZE = 20;

const Container = styled.div`
  padding: 20px;
`;

const Title = styled(Typography)`
  font-family: 'Carter One', cursive !important;
  font-weight: 400 !important;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
`;

const Status = styled(Box)`
  padding: 24px;
  text-align: center;
  color: ${props => props.theme.colors.secondary};
`;

function KriticalWatchingPage({ user }) {
  const [movies, setMovies] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const observer = useRef();
  const userId = user?.id;

  useEffect(() => {
    if (!userId) return undefined;

    let cancelled = false;
    const loadMovies = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getKriticalWatching(userId, page, PAGE_SIZE);
        if (cancelled) return;

        setMovies(previous => {
          if (page === 1) return data;
          const existingIds = new Set(previous.map(movie => movie.id));
          return [...previous, ...data.filter(movie => !existingIds.has(movie.id))];
        });
        setHasMore(data.length === PAGE_SIZE);
      } catch (err) {
        if (!cancelled) setError('Failed to load Kritical Watching recommendations.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadMovies();
    return () => {
      cancelled = true;
    };
  }, [page, refreshVersion, userId]);

  const lastMovieElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(previousPage => previousPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [hasMore, loading]);

  const handleRatingUpdate = movieId => {
    setMovies(previous => previous.filter(movie => movie.id !== movieId));
    setPage(1);
    setHasMore(true);
    setRefreshVersion(version => version + 1);
  };

  return (
    <Container>
      <Box sx={{ mb: 3 }}>
        <Title variant="h4">Kritical Watching</Title>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          More movies recommended from the Kritics who best match your taste.
        </Typography>
      </Box>

      <Grid>
        {movies.map((movie, index) => (
          <div
            key={movie.id}
            ref={index === movies.length - 1 ? lastMovieElementRef : null}
          >
            <MovieCard movie={movie} onRatingUpdate={handleRatingUpdate} />
          </div>
        ))}
      </Grid>

      {error && <Status>{error}</Status>}
      {loading && <Status>Loading...</Status>}
      {!loading && !error && movies.length === 0 && (
        <Status>No Kritical Watching recommendations are available yet.</Status>
      )}
      {!loading && !hasMore && movies.length > 0 && (
        <Status>You've reached the end of your Kritical Watching recommendations.</Status>
      )}
    </Container>
  );
}

export default KriticalWatchingPage;
